/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm } from "react-hook-form";
import { Button, Sidebar, SelectButton, MultipleSelect } from "@nordcloud/gnui";
import { useShowError } from "~/components/Error/useShowError";
import { Fieldset } from "~/components/Forms";
import { ServiceAttachment } from "~/components/Services/types";
import FlexContainer from "~/components/Styled/FlexContainer";
import { showSuccess } from "~/components/Toast";
import { ACTIONS, Can } from "~/routing";
import { useAxios, generateActionSuccessText } from "~/tools";
import { ServiceGroupSelect } from "./ServiceGroupSelect";
import { ServiceSelect } from "./ServiceSelect";

type ServiceAndGroupAssignProps = {
  envId: string;
  refetch: () => void;
  serviceAttachments: ServiceAttachment[];
};
function ServiceAndGroupAssign({
  envId,
  refetch,
  serviceAttachments,
}: ServiceAndGroupAssignProps) {
  const [isService, setIsService] = React.useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const [selectedId, setSelectedId] = React.useState("");
  const { showError } = useShowError();
  const { Axios } = useAxios();

  const onSubmit = async () => {
    try {
      isService
        ? await Axios().post(`/environments/${envId}/services`, {
            serviceId: selectedId,
          })
        : await Axios().post(`/environments/${envId}/serviceGroups`, {
            serviceGroupId: selectedId,
          });

      setIsSidebarOpen(false);
      refetch();
      showSuccess(
        generateActionSuccessText(
          `${isService ? "Services" : "Service Group"}`
        )()("assigned")()
      );
    } catch (e) {
      showError(e.response);
    } finally {
      setSelectedId("");
    }
  };
  const { handleSubmit } = useForm();

  return (
    <>
      <Can action={ACTIONS.ADD_APPLICATION_SERVICE}>
        <Button
          onClick={() => setIsSidebarOpen(true)}
          css={{ marginLeft: "auto", marginBottom: "1rem" }}
          icon="link"
        >
          Assign
        </Button>
      </Can>
      <Sidebar
        width="30rem"
        title="Assign Service or Service Group"
        isOpen={isSidebarOpen}
        onClick={() => setIsSidebarOpen(false)}
      >
        <Fieldset>
          <MultipleSelect size="small">
            <SelectButton
              name="button1"
              value="service"
              labelText="Service"
              isActive={isService}
              onClick={() => {
                setIsService(true);
                setSelectedId("");
              }}
            />
            <SelectButton
              name="button2"
              value="serviceGroup"
              labelText="Service Group"
              isActive={!isService}
              onClick={() => {
                setIsService(false);
                setSelectedId("");
              }}
            />
          </MultipleSelect>
        </Fieldset>
        {isService ? (
          <ServiceSelect
            selectedId={selectedId}
            setSelectedId={setSelectedId}
            serviceAttachments={serviceAttachments}
          />
        ) : (
          <ServiceGroupSelect
            selectedId={selectedId}
            setSelectedId={setSelectedId}
            serviceAttachments={serviceAttachments}
          />
        )}
        <FlexContainer>
          <Button onClick={handleSubmit(onSubmit)} disabled={!selectedId}>
            Assign
          </Button>
          <Button
            severity="medium"
            type="button"
            css={{ marginLeft: "auto" }}
            onClick={() => {
              setIsSidebarOpen(false);
              setSelectedId("");
            }}
          >
            Cancel
          </Button>
        </FlexContainer>
      </Sidebar>
    </>
  );
}

export { ServiceAndGroupAssign };
